import React, { useEffect, useState } from "react";
import { Badge, Button, Spinner, Select } from "@/components/ui";
import { endpoints } from "@/lib/api";
import { fmt } from "@/lib/format";
import { X, Users, Linkedin, Mail, Calculator, FileText, RefreshCw, Landmark, Briefcase, Building2, Send, Copy, Check, Banknote, TrendingUp, Globe, Leaf, Sparkles } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Gavel, Eye } from "lucide-react";
import { InvestorDealMatches } from "@/components/InvestorMatch";

export const CATEGORY_LABELS = {
  extraordinary_finance: { label: "Extraordinary Finance", tone: "gold" },
  generational_succession: { label: "Generational Succession", tone: "yellow" },
  investor_search: { label: "Investor Search", tone: "navy" },
  internationalization: { label: "Internationalization", tone: "green" },
};

const mn = (v) => (v ? `€${(v / 1e6).toFixed(0)}M` : "n/a");
const mn1 = (v) => (v ? `€${(v / 1e6).toFixed(1)}M` : "n/a");

// ---------- Outreach Email drawer ----------
function OutreachEmailPanel({ companyId, dmIndex, dm, onClose }) {
  const [loading, setLoading] = useState(true);
  const [tone, setTone] = useState("formal");
  const [draft, setDraft] = useState(null);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);

  const generate = async (t = tone) => {
    setLoading(true); setErr(null);
    try {
      const r = await endpoints.outreachEmail(companyId, dmIndex, { tone: t });
      setDraft(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { generate(); /* eslint-disable-next-line */ }, [companyId, dmIndex]);

  const copyAll = async () => {
    if (!draft) return;
    await navigator.clipboard.writeText(`Subject: ${draft.subject}\n\n${draft.body}`);
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  const mailtoHref = draft
    ? `mailto:${draft.to || ""}?subject=${encodeURIComponent(draft.subject)}&body=${encodeURIComponent(draft.body)}`
    : "#";

  return (
    <div className="fixed inset-0 z-[60] flex justify-center items-start pt-20 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="outreach-email-overlay">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-[#0B1120] border border-[#C5A059]/30 rounded-md p-6 md:p-7 shadow-2xl mx-4" data-testid="outreach-email-panel">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline flex items-center gap-1.5"><Send size={12} className="text-gold" /> Outreach Email · First contact</div>
            <div className="font-display text-lg mt-1">{dm.full_name}</div>
            <div className="text-[11px] text-white/50">{dm.role} · {dm.email || "email not public"}</div>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="outreach-close"><X size={18} /></button>
        </div>

        <div className="flex gap-2 mb-4">
          <div className="flex-1">
            <label className="text-[10px] text-white/40 mb-1 block">Tone</label>
            <Select value={tone} onChange={(e) => { setTone(e.target.value); generate(e.target.value); }} data-testid="outreach-tone">
              <option value="formal">Formal · institutional</option>
              <option value="direct">Direct · concise</option>
              <option value="warm">Warm · relational</option>
            </Select>
          </div>
          <div className="flex items-end">
            <Button variant="outline" size="sm" onClick={() => generate()} disabled={loading} data-testid="outreach-regenerate">
              {loading ? <Spinner size={11} /> : <><Sparkles size={11} /> Regenerate</>}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="py-10 text-center text-white/50 text-[12px]"><Spinner size={14} /> CLARA is drafting the personalised email…</div>
        ) : err ? (
          <div className="py-6 text-center text-red-400 text-[12px]" data-testid="outreach-error">Error: {err}</div>
        ) : draft ? (
          <div data-testid="outreach-draft">
            <div className="clara-surface-2 p-3 rounded-md mb-3">
              <div className="text-[10px] text-white/40 mb-1">Subject</div>
              <div className="text-[13px] font-medium" data-testid="outreach-subject">{draft.subject}</div>
            </div>
            <div className="clara-surface-2 p-3 rounded-md">
              <div className="text-[10px] text-white/40 mb-1">Body</div>
              <pre className="text-[12px] whitespace-pre-wrap font-sans leading-relaxed text-white/90" data-testid="outreach-body">{draft.body}</pre>
            </div>
            <div className="flex gap-2 mt-4 flex-wrap">
              <Button size="sm" onClick={copyAll} data-testid="outreach-copy">
                {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy subject + body</>}
              </Button>
              <a href={mailtoHref} target="_blank" rel="noopener noreferrer">
                <Button size="sm" variant="outline" data-testid="outreach-open-mailto"><Mail size={12} /> Open in email client</Button>
              </a>
            </div>
            <div className="text-[9px] text-white/25 mt-3">Generated by CLARA Origin · Claude Sonnet 4.5 · Verify tone and facts before sending</div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ---------- Decision maker row ----------
function DecisionMakerRow({ p, i, onDraftEmail }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-white/5 last:border-0" data-testid={`decision-maker-${i}`}>
      <div className="w-8 h-8 rounded-full bg-[#C5A059]/15 border border-[#C5A059]/30 flex items-center justify-center text-[11px] font-mono text-gold shrink-0">
        {p.full_name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium">{p.full_name}</div>
        <div className="text-[11px] text-white/50">{p.role}</div>
        <div className="flex gap-3 mt-1.5 flex-wrap">
          {p.linkedin_url ? (
            <a href={p.linkedin_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] text-[#93c5fd] hover:text-white" data-testid={`dm-linkedin-${i}`}>
              <Linkedin size={11} /> LinkedIn
            </a>
          ) : <span className="text-[11px] text-white/25 inline-flex items-center gap-1"><Linkedin size={11} /> n/a</span>}
          {p.email ? (
            <a href={`mailto:${p.email}`} className="inline-flex items-center gap-1 text-[11px] text-gold hover:text-white" data-testid={`dm-email-${i}`}>
              <Mail size={11} /> {p.email}
            </a>
          ) : <span className="text-[11px] text-white/25 inline-flex items-center gap-1"><Mail size={11} /> email not public</span>}
          <button onClick={() => onDraftEmail(i, p)} className="inline-flex items-center gap-1 text-[11px] text-gold hover:text-white underline-offset-2 hover:underline" data-testid={`dm-draft-email-${i}`}>
            <Send size={11} /> Draft email
          </button>
        </div>
      </div>
      <Badge tone={p.confidence === "high" ? "green" : p.confidence === "medium" ? "yellow" : "neutral"}>{p.confidence}</Badge>
    </div>
  );
}

function InvestorProfile({ p }) {
  if (!p) return null;
  const cell = (l, v) => (
    <div className="clara-surface-2 p-2.5 rounded-md">
      <div className="overline text-[9px] mb-0.5">{l}</div>
      <div className="text-[12px] font-mono">{v}</div>
    </div>
  );
  return (
    <div className="mb-5" data-testid="investor-profile">
      <div className="overline mb-2 flex items-center gap-1.5"><Landmark size={12} className="text-gold" /> Investor Profile</div>
      <div className="grid grid-cols-2 gap-2">
        {cell("Type", p.investor_type)}
        {cell("AUM", mn(p.aum_eur))}
        {cell("Ticket", `${mn(p.ticket_min_eur)} – ${mn(p.ticket_max_eur)}`)}
        {cell("Stage", p.stage_focus)}
        {cell("Geo focus", p.geo_focus)}
        {cell("Sectors", (p.sector_focus || []).join(", ") || "Generalist")}
      </div>
      {!!(p.recent_deals || []).length && (
        <div className="mt-3">
          <div className="text-[10px] text-white/40 mb-1">Recent deals</div>
          <ul className="text-[11px] text-white/70 space-y-0.5 list-disc pl-4">{p.recent_deals.map((d, i) => <li key={i}>{d}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

// ---------- Enrichment section (generic) ----------
function EnrichmentSection({ title, icon: Icon, kind, data, status, onRun, running, testid, children }) {
  const isReady = data && data.status === "ready";
  return (
    <div className="clara-surface-2 rounded-md p-4 mb-4 border-l-2 border-[#C5A059]/40" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="overline flex items-center gap-1.5"><Icon size={12} className="text-gold" /> {title}</div>
        <Button size="sm" variant="outline" onClick={onRun} disabled={running} data-testid={`${testid}-run`}>
          {running ? <><Spinner size={11} /> Researching…</> : <><RefreshCw size={11} /> {isReady ? "Refresh" : "Run deep research"}</>}
        </Button>
      </div>
      {status === "searching" ? (
        <div className="text-[11px] text-white/40 py-3 flex items-center gap-2"><Spinner size={11} /> Deep research in progress…</div>
      ) : isReady ? (
        <div className="text-[12px] text-white/85 leading-relaxed">{children}</div>
      ) : status === "no_evidence" || status === "error" ? (
        <div className="text-[11px] text-white/40 py-3">No relevant public data found. You can retry or consult direct sources.</div>
      ) : (
        <div className="text-[11px] text-white/40 py-3">Not run yet. Click "Run deep research" to launch Tavily + Claude.</div>
      )}
    </div>
  );
}

function BankDebtBody({ bd }) {
  const peer = bd.peer_benchmark || {};
  const posColor = { below_median: "text-emerald-400", in_line: "text-white/70", stretched: "text-amber-400", distressed: "text-red-400" }[peer.position_vs_peers] || "text-white/50";
  return (
    <>
      <p className="mb-2 text-white/70 italic">{bd.narrative}</p>
      <div className="grid grid-cols-3 gap-2 mb-2">
        <div className="clara-surface p-2 rounded"><div className="text-[9px] text-white/40 uppercase">Net debt</div><div className="text-[12px] font-mono">{bd.net_debt_eur_mn ? `€${bd.net_debt_eur_mn.toFixed(1)}M` : "n/a"}</div></div>
        <div className="clara-surface p-2 rounded"><div className="text-[9px] text-white/40 uppercase">ND/EBITDA</div><div className="text-[12px] font-mono">{bd.net_debt_ebitda != null ? `${bd.net_debt_ebitda.toFixed(2)}x` : "n/a"}</div></div>
        <div className="clara-surface p-2 rounded"><div className="text-[9px] text-white/40 uppercase">Bond</div><div className="text-[12px] font-mono">{bd.bond_outstanding_eur_mn ? `€${bd.bond_outstanding_eur_mn.toFixed(1)}M` : "n/a"}</div></div>
      </div>
      {!!(bd.creditor_banks || []).length && (
        <div className="mb-2"><span className="text-[10px] text-white/40 uppercase">Banks: </span><span className="text-[11px]">{bd.creditor_banks.join(", ")}</span></div>
      )}
      {peer.position_vs_peers && (
        <div className="text-[11px] mb-2">
          <span className="text-white/40">Peer benchmark: </span>
          <span className={`font-semibold uppercase ${posColor}`}>{peer.position_vs_peers.replace(/_/g, " ")}</span>
          {peer.industry_median_net_debt_ebitda && <span className="text-white/50"> · median {peer.industry_median_net_debt_ebitda.toFixed(2)}x</span>}
        </div>
      )}
      {!!(bd.stress_flags || []).length && (
        <div className="text-[11px]"><span className="text-white/40 uppercase text-[10px]">Stress flags:</span>
          <ul className="list-disc pl-4 mt-1 space-y-0.5 text-white/70">{bd.stress_flags.map((f, i) => <li key={i}>{f}</li>)}</ul>
        </div>
      )}
      <div className="text-[10px] mt-2"><span className="text-white/40">Refinancing opportunity: </span><span className="uppercase text-gold font-semibold">{bd.refinancing_opportunity || "none"}</span></div>
    </>
  );
}

function InvestmentHistoryBody({ ih }) {
  const rounds = ih.rounds || [];
  const inv = ih.current_investors || [];
  return (
    <>
      <p className="mb-2 text-white/70 italic">{ih.narrative}</p>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="clara-surface p-2 rounded"><div className="text-[9px] text-white/40 uppercase">Total raised</div><div className="text-[12px] font-mono">{mn1(ih.total_raised_eur_mn * 1e6)}</div></div>
        <div className="clara-surface p-2 rounded"><div className="text-[9px] text-white/40 uppercase">Last valuation</div><div className="text-[12px] font-mono">{mn1(ih.last_valuation_eur_mn * 1e6)}</div></div>
        <div className="clara-surface p-2 rounded"><div className="text-[9px] text-white/40 uppercase">Founded</div><div className="text-[12px] font-mono">{ih.founded_year || "n/a"}</div></div>
      </div>
      {!!rounds.length && (
        <div className="mb-3">
          <div className="text-[10px] text-white/40 uppercase mb-1">Rounds</div>
          <div className="space-y-1">
            {rounds.map((r, i) => (
              <div key={i} className="flex justify-between text-[11px] py-1 border-b border-white/5 last:border-0">
                <span><span className="text-gold font-mono">{r.year}</span> · {r.round} — {r.lead_investor || "—"}</span>
                <span className="font-mono text-white/70">{r.amount_eur_mn ? `€${r.amount_eur_mn.toFixed(1)}M` : "n/a"}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {!!inv.length && (
        <div>
          <div className="text-[10px] text-white/40 uppercase mb-1">Current investors</div>
          <div className="space-y-0.5">
            {inv.map((x, i) => (
              <div key={i} className="flex justify-between text-[11px]">
                <span>{x.name} <span className="text-white/40">({x.type})</span></span>
                <span className="font-mono">{x.stake_pct != null ? `${x.stake_pct.toFixed(1)}%` : "n/a"}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function InternationalizationBody({ intl }) {
  const scoreColor = intl.potential_score >= 70 ? "text-emerald-400" : intl.potential_score >= 40 ? "text-amber-400" : "text-white/60";
  return (
    <>
      <p className="mb-2 text-white/70 italic">{intl.narrative}</p>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="clara-surface p-2 rounded"><div className="text-[9px] text-white/40 uppercase">Stage</div><div className="text-[11px] font-mono">{(intl.current_stage || "").replace(/_/g, " ")}</div></div>
        <div className="clara-surface p-2 rounded"><div className="text-[9px] text-white/40 uppercase">Export %</div><div className="text-[12px] font-mono">{intl.export_revenue_pct != null ? `${intl.export_revenue_pct.toFixed(0)}%` : "n/a"}</div></div>
        <div className="clara-surface p-2 rounded"><div className="text-[9px] text-white/40 uppercase">Potential</div><div className={`text-[13px] font-mono font-semibold ${scoreColor}`}>{intl.potential_score ?? "n/a"}/100</div></div>
      </div>
      {!!(intl.current_foreign_markets || []).length && (
        <div className="text-[11px] mb-1"><span className="text-white/40">Current markets: </span>{intl.current_foreign_markets.join(", ")}</div>
      )}
      {!!(intl.target_markets || []).length && (
        <div className="text-[11px] mb-2"><span className="text-white/40">Target markets: </span><span className="text-gold">{intl.target_markets.join(", ")}</span></div>
      )}
      {!!(intl.expansion_signals || []).length && (
        <div className="text-[11px]"><span className="text-white/40 uppercase text-[10px]">Signals:</span>
          <ul className="list-disc pl-4 mt-1 space-y-0.5 text-white/70">{intl.expansion_signals.map((f, i) => <li key={i}>{f}</li>)}</ul>
        </div>
      )}
      {intl.financing_needs && <div className="text-[11px] mt-2"><span className="text-white/40">Financing needs: </span>{intl.financing_needs}</div>}
    </>
  );
}

function EsgBadge({ esg }) {
  if (!esg) return null;
  const map = {
    none: { tone: "red", label: "No ESG" },
    basic: { tone: "yellow", label: "Basic ESG" },
    structured: { tone: "green", label: "ESG structured" },
    unknown: { tone: "neutral", label: "ESG unknown" },
  };
  const m = map[esg.status] || map.unknown;
  return <Badge tone={m.tone} className="inline-flex items-center gap-1"><Leaf size={10} /> {m.label}</Badge>;
}

// ---------- Main drawer ----------
export function CompanyDrawer({ company, onClose, onUpdated }) {
  const [c, setC] = useState(company);
  const [hunting, setHunting] = useState(false);
  const [valuations, setValuations] = useState([]);
  const [outreach, setOutreach] = useState(null); // { dmIndex, dm }
  const [running, setRunning] = useState({});     // { bank_debt: bool, ... }
  const [dds, setDds] = useState([]);
  const [ddBusy, setDdBusy] = useState(false);
  const [watchMsg, setWatchMsg] = useState("");
  const watch = async () => {
    try { await endpoints.committeeWatchAdd(c.id); setWatchMsg("Added to Committee Watchlist"); }
    catch (e) { setWatchMsg(e?.response?.data?.detail || e.message); }
  };
  const navigate = useNavigate();

  useEffect(() => { setC(company); }, [company]);
  useEffect(() => {
    if (company?.id) endpoints.ddList(company.id).then((r) => setDds(r.data)).catch(() => setDds([]));
  }, [company]);

  const runDD = async () => {
    setDdBusy(true);
    try {
      const r = await endpoints.ddCreate(c.id);
      navigate(`/dd/${r.data.id}`);
    } catch (e) {
      alert("Due diligence failed: " + (e?.response?.data?.detail || e.message));
    } finally { setDdBusy(false); }
  };
  useEffect(() => {
    if (!company) return;
    endpoints.valuations().then((r) => setValuations(r.data.filter((v) => v.company_id === company.id))).catch(() => {});
  }, [company]);

  if (!c) return null;
  const cat = CATEGORY_LABELS[c.category];
  const isInvestor = c.entity_type === "investor";

  const hunt = async () => {
    setHunting(true);
    try {
      const r = await endpoints.findDecisionMakers(c.id);
      const next = { ...c, decision_makers: r.data.decision_makers, decision_makers_status: r.data.status, decision_makers_updated_at: r.data.updated_at };
      setC(next);
      onUpdated?.(next);
    } catch (e) {
      alert("Decision makers search failed: " + (e?.response?.data?.detail || e.message));
    } finally { setHunting(false); }
  };

  const runEnrichment = async (kind) => {
    setRunning((s) => ({ ...s, [kind]: true }));
    try {
      const r = await endpoints.enrichDeep(c.id, kind);
      const next = { ...c, [kind]: r.data.result };
      setC(next);
      onUpdated?.(next);
    } catch (e) {
      alert(`Enrichment ${kind} failed: ` + (e?.response?.data?.detail || e.message));
    } finally { setRunning((s) => ({ ...s, [kind]: false })); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="company-drawer-overlay">
      <aside onClick={(e) => e.stopPropagation()} className="w-full max-w-xl h-full overflow-y-auto bg-[#0B1120] border-l border-white/10 p-6 md:p-8 animate-in slide-in-from-right duration-300" data-testid="company-drawer">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="overline flex items-center gap-2">
              {isInvestor ? <Landmark size={12} className="text-gold" /> : <Building2 size={12} className="text-gold" />}
              {isInvestor ? "Investor" : "Target Company"}
            </div>
            <h2 className="font-display text-2xl mt-1" data-testid="drawer-company-name">{c.name}</h2>
            <div className="flex gap-1.5 mt-2 flex-wrap">
              {cat && <Badge tone={cat.tone}>{cat.label}</Badge>}
              <Badge tone="neutral">{c.sector}</Badge>
              <Badge tone="neutral">{c.region || c.country}</Badge>
              {c.ticker && <Badge tone="gold">{c.ticker}</Badge>}
              <EsgBadge esg={c.esg} />
            </div>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="drawer-close"><X size={18} /></button>
        </div>

        {c.description && <p className="text-[12px] text-white/60 leading-relaxed mb-5">{c.description}</p>}

        {!isInvestor && (
          <div className="grid grid-cols-3 gap-2 mb-5">
            {[["Revenue", fmt.money(c.revenue)], ["EBITDA", fmt.money(c.ebitda)], ["Margin", fmt.pct(c.ebitda_margin)]].map(([l, v]) => (
              <div key={l} className="clara-surface-2 p-2.5 rounded-md"><div className="overline text-[9px] mb-0.5">{l}</div><div className="text-[13px] font-mono">{v}</div></div>
            ))}
          </div>
        )}

        {isInvestor && <InvestorProfile p={c.investor_profile} />}

        {c.origin_signals && Object.keys(c.origin_signals).length > 0 && (
          <div className="mb-5" data-testid="origin-signals">
            <div className="overline mb-2">Origination signals</div>
            <div className="space-y-1">
              {Object.entries(c.origin_signals).map(([k, v]) => (
                <div key={k} className="flex gap-2 text-[11px]"><span className="text-white/40 w-32 shrink-0 capitalize">{k.replace(/_/g, " ")}</span><span className="text-white/80">{String(v)}</span></div>
              ))}
            </div>
          </div>
        )}

        <div className="clara-surface-2 rounded-md p-4 mb-5 border-l-2 border-[#C5A059]" data-testid="decision-makers-section">
          <div className="flex items-center justify-between mb-2">
            <div className="overline flex items-center gap-1.5"><Users size={12} className="text-gold" /> Decision Makers</div>
            <Button size="sm" variant="outline" onClick={hunt} disabled={hunting} data-testid="find-decision-makers-btn">
              {hunting ? <><Spinner size={11} /> Searching web…</> : <><RefreshCw size={11} /> {c.decision_makers?.length ? "Refresh" : "Find via Tavily"}</>}
            </Button>
          </div>
          {c.decision_makers_status === "searching" || c.decision_makers_status === "pending" ? (
            <div className="text-[11px] text-white/40 py-3 flex items-center gap-2"><Spinner size={11} /> Background decision-makers search in progress…</div>
          ) : c.decision_makers?.length ? (
            <div>{c.decision_makers.map((p, i) => (
              <DecisionMakerRow key={i} p={p} i={i} onDraftEmail={(idx, dm) => setOutreach({ dmIndex: idx, dm })} />
            ))}</div>
          ) : (
            <div className="text-[11px] text-white/40 py-3">
              {c.decision_makers_status === "ready" || c.decision_makers_status === "error"
                ? "No publicly verifiable executive contacts found. Recommend outreach via PEC / advisor."
                : "No search executed yet. Click \"Find via Tavily\" to identify CEO, CFO, partners and owners with public LinkedIn and email."}
            </div>
          )}
          {c.decision_makers_updated_at && <div className="text-[9px] text-white/25 mt-2">Updated {fmt.date(c.decision_makers_updated_at)} · fonte: Tavily web search + Claude extraction</div>}
        </div>

        {!isInvestor && (
          <>
            <EnrichmentSection title="Bank Debt Analysis" icon={Banknote} kind="bank_debt" data={c.bank_debt} status={c.bank_debt?.status}
              onRun={() => runEnrichment("bank_debt")} running={!!running.bank_debt} testid="bank-debt-section">
              {c.bank_debt && <BankDebtBody bd={c.bank_debt} />}
            </EnrichmentSection>

            <EnrichmentSection title="Investment History" icon={TrendingUp} kind="investment_history" data={c.investment_history} status={c.investment_history?.status}
              onRun={() => runEnrichment("investment_history")} running={!!running.investment_history} testid="investment-history-section">
              {c.investment_history && <InvestmentHistoryBody ih={c.investment_history} />}
            </EnrichmentSection>

            <EnrichmentSection title="Internationalization" icon={Globe} kind="internationalization" data={c.internationalization} status={c.internationalization?.status}
              onRun={() => runEnrichment("internationalization")} running={!!running.internationalization} testid="internationalization-section">
              {c.internationalization && <InternationalizationBody intl={c.internationalization} />}
            </EnrichmentSection>
          </>
        )}

        <div className="flex gap-2 flex-wrap">
          {valuations.length ? (
            <Link to={`/valuations?vid=${valuations[0].id}`}><Button size="sm" data-testid="drawer-open-dossier"><FileText size={12} /> {isInvestor ? "Investor Dossier" : "M&A Dossier"}</Button></Link>
          ) : null}
          <a href={valuations.length ? endpoints.valuationPdfUrl(valuations[0].id) : undefined} target="_blank" rel="noopener noreferrer">
            {valuations.length ? <Button size="sm" variant="outline"><Calculator size={12} /> PDF</Button> : null}
          </a>
          <Link to="/deals"><Button size="sm" variant="ghost"><Briefcase size={12} /> Deals</Button></Link>
        </div>

        {isInvestor && (
          <div className="mt-5 pt-4 border-t border-white/5">
            <div className="overline mb-2">Best-fit deals (Investor Match Score)</div>
            <InvestorDealMatches investorId={c.id} />
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-white/5 flex gap-2 flex-wrap" data-testid="drawer-dd-actions">
          <Button size="sm" variant="navy" onClick={runDD} disabled={ddBusy} data-testid="drawer-run-dd">
            {ddBusy ? <Spinner size={12} /> : <ShieldCheck size={12} />} {ddBusy ? "Building checklist…" : "Run Due Diligence"}
          </Button>
          {dds.length > 0 && (
            <Link to={`/dd/${dds[0].id}`}><Button size="sm" variant="outline" data-testid="drawer-open-dd"><FileText size={12} /> Latest DD ({String(dds[0].created_at).slice(0, 10)})</Button></Link>
          )}
          <Link to={`/ask?committee=${encodeURIComponent(c.name)}`}><Button size="sm" variant="outline" data-testid="drawer-run-committee"><Gavel size={12} /> Deal Committee</Button></Link>
          {!isInvestor && <Button size="sm" variant="ghost" onClick={watch} data-testid="drawer-watch-committee"><Eye size={12} /> Watch Committee</Button>}
          {watchMsg && <div className="w-full text-[11px] text-gold" data-testid="drawer-watch-msg">{watchMsg}</div>}
        </div>

        {outreach && (
          <OutreachEmailPanel companyId={c.id} dmIndex={outreach.dmIndex} dm={outreach.dm} onClose={() => setOutreach(null)} />
        )}
      </aside>
    </div>
  );
}
